import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { ManagedDashboardService } from '../../../services/managedDashboard.service';
import { ManagedPerson } from '../../../interfaces/managedDashboard.interface';
import { displayUsername } from '../../../utils/userUtils';

/** Heads, sales managers and admins: the people whose boards the caller may edit (the backend decides). */
export default function TeamDashboardsPage() {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const [people, setPeople] = useState<ManagedPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');

    useEffect(() => {
        if (!institutionId) { setLoading(false); return; }
        let live = true;
        ManagedDashboardService.people(institutionId)
            .then(p => { if (live) setPeople(p); })
            .catch(e => { if (live) toast.showApiError?.(e, 'Could not load your team'); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return !needle ? people : people.filter(p => `${p.name ?? ''} ${p.username} ${(p.roles ?? []).join(' ')}`.toLowerCase().includes(needle));
    }, [people, q]);

    return (
        <Box sx={{ p: 2, minWidth: 0 }} data-help-page="dashboard.team">
            <Typography variant="h5" fontWeight={700} gutterBottom>Team dashboards</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-help-section="dashboard.team.intro">
                Choose a person to change which widgets are on their board. You see the layout only, never their data.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box data-help="dashboard.team.search" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                    <TextField label="Search people" placeholder="Name, username or role" size="small" fullWidth value={q} onChange={e => setQ(e.target.value)} />
                </Box>
            </Box>
            {loading ? <CircularProgress size={24} /> : (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }} data-help-section="dashboard.team.people">
                    <Table size="small" data-testid="team-table">
                        <TableHead><TableRow><TableCell data-help="dashboard.team.col.name">Name</TableCell><TableCell data-help="dashboard.team.col.username">Username</TableCell><TableCell data-help="dashboard.team.col.role">Role</TableCell><TableCell data-help="dashboard.team.col.board" align="right">Board</TableCell></TableRow></TableHead>
                        <TableBody>
                            {shown.length === 0 && <TableRow><TableCell colSpan={4}>No people in your scope.</TableCell></TableRow>}
                            {shown.map(p => (
                                <TableRow key={p.userId}>
                                    <TableCell>{p.name || displayUsername(p.username)}</TableCell>
                                    <TableCell>{displayUsername(p.username)}</TableCell>
                                    <TableCell>{p.roles?.length ? p.roles.join(', ') : '—'}</TableCell>
                                    <TableCell align="right">
                                        <Button data-help="dashboard.team.edit" size="small" component={RouterLink} to={`/dashboard/team/${p.userId}`}>Edit dashboard</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
